import React from 'react';
import dayjs from 'dayjs';
import { ArrowsClockwise } from '@phosphor-icons/react';
import Typhography from '../../../shared/Typhography/Typhography';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import styles from './StatsFiltersBar.module.scss';
import type { PeriodPreset } from '../hooks/useTrainingStatistics';

/**
 * Barra filtri globali Dashboard Statistiche.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2759-2773.
 */
interface StatsFiltersBarProps {
  preset: PeriodPreset;
  onPresetChange: (p: PeriodPreset) => void;
  customStart?: string;
  customEnd?: string;
  onCustomRange?: (start: string, end: string) => void;
  onRecalculate?: () => void;
  isRecalculating?: boolean;
  compareEnabled?: boolean;
  onCompareToggle?: (enabled: boolean) => void;
  athleteFilter?: string;
  onAthleteFilterChange?: (athleteId: string) => void;
  availableAthletes?: Array<{ _id: string; firstName?: string; lastName?: string }>;
}

const PRESET_LABELS: Record<PeriodPreset, string> = {
  '7d': 'Ultimi 7 giorni',
  '30d': 'Ultimi 30 giorni',
  '3m': 'Ultimi 3 mesi',
  'season': 'Stagione',
  'custom': 'Personalizzato',
};

const PRESETS: PeriodPreset[] = ['7d', '30d', '3m', 'season', 'custom'];

const StatsFiltersBar: React.FC<StatsFiltersBarProps> = ({
  preset,
  onPresetChange,
  customStart,
  customEnd,
  onCustomRange,
  onRecalculate,
  isRecalculating = false,
  compareEnabled = false,
  onCompareToggle,
  athleteFilter,
  onAthleteFilterChange,
  availableAthletes = [],
}) => {
  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <Typhography size="bold-12" className={styles.filterLabel}>PERIODO</Typhography>
        <div className={styles.presetGroup}>
          {PRESETS.map((p) => (
            <button
              key={p}
              className={[styles.presetBtn, preset === p ? styles.active : ''].filter(Boolean).join(' ')}
              onClick={() => onPresetChange(p)}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>

        {preset === 'custom' && onCustomRange && (
          <div className={styles.customRange}>
            <input
              type="date"
              className={styles.dateInput}
              value={customStart ? customStart.slice(0, 10) : ''}
              onChange={(e) => onCustomRange(dayjs(e.target.value).toISOString(), customEnd ?? dayjs().toISOString())}
            />
            <span className={styles.dateSep}>→</span>
            <input
              type="date"
              className={styles.dateInput}
              value={customEnd ? customEnd.slice(0, 10) : ''}
              onChange={(e) => onCustomRange(customStart ?? dayjs().toISOString(), dayjs(e.target.value).toISOString())}
            />
          </div>
        )}
      </div>

      <div className={styles.right}>
        {onAthleteFilterChange && (
          <div className={styles.filterGroup}>
            <Typhography size="bold-12" className={styles.filterLabel}>ATLETA</Typhography>
            <select
              className={styles.dateInput}
              value={athleteFilter ?? ''}
              onChange={(e) => onAthleteFilterChange(e.target.value)}
            >
              <option value="">Tutti</option>
              {availableAthletes.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.lastName ?? ''} {a.firstName?.[0] ?? ''}.
                </option>
              ))}
            </select>
          </div>
        )}

        {onCompareToggle && (
          <label className={styles.compareToggle}>
            <input
              type="checkbox"
              checked={compareEnabled}
              onChange={(e) => onCompareToggle(e.target.checked)}
            />
            <span>Confronta periodo precedente</span>
          </label>
        )}

        {onRecalculate && (
          <CustomButton
            size="sm"
            color="outline"
            label={isRecalculating ? 'Ricalcolo...' : 'Aggiorna'}
            disabled={isRecalculating}
            onClick={onRecalculate}
          >
            <ArrowsClockwise size={14} weight={isRecalculating ? 'regular' : 'bold'} />
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export default StatsFiltersBar;
